import * as React from "react";
import * as routes from "../routes";
import { Badge, BoldSpan, Button, Link, Modal, PageLayout, withAnalytics, withFeatureFlags, withGraphqlErrorFlags, withLoadingSpinner, withServiceNotFoundHandler, withShowFlag, compose, graphql, } from "@atlassiansox/microscopekit";
import { formatDate, prettifyCheckName } from "../../helpers";
import CheckCircleIcon from "@atlaskit/icon/glyph/check-circle";
import Banner from '@atlaskit/banner';
import SendIcon from '@atlaskit/icon/glyph/send';
import { CheckName } from "./check-name";
import CrossCircleIcon from "@atlaskit/icon/glyph/cross-circle";
import ErrorIcon from "@atlaskit/icon/glyph/error";
import { Exemptions } from "./exemptions";
import JiraFailedBuildStatusIcon from "@atlaskit/icon/glyph/jira/failed-build-status";
import RefreshIcon from "@atlaskit/icon/glyph/refresh";
import SettingsIcon from "@atlaskit/icon/glyph/settings";
import sendLinterJobExecReqMutation from "../../graphql/send-linter-job-exec-req.mutation.graphql";
import serviceLinterScoresQuery from "../../graphql/service-linter-scores.query.graphql";
import styled from "styled-components";
const DynamicTable = require("@atlaskit/dynamic-table").default;
const { ButtonGroup } = require("@atlaskit/button");
const ScoreSpan = styled.span `
  font-size: 4rem;
  ${(props) => `color: ${props.color}`};
`;
const CheckDetails = styled.span `
  color: #8993a4;
  padding-bottom: 0.3rem;
`;
const CheckScore = styled.span `
  display: block;
  width: 100%;
  text-align: right;
`;
const TableIcon = styled.div `
  text-align: center;
`;
const OverallScore = styled.div `
  margin-bottom: 0px;
`;
const ControlButtonDiv = styled.div `
  margin-right: 10px;
`;
const CheckCounter = styled.div `
  font-size: 1rem;
`;
// Outcome constants as defined by sltf
const OUTCOME_COMPLETE = 0;
const OUTCOME_ERROR = 1;
const OUTCOME_NOT_APPLICABLE = 2;
export const getScorePercent = (score, max) => {
    let percent = max === 0 ? 100 : Math.floor((score / max) * 100);
    return percent < 0 ? 0 : percent;
};
const convertLinterExemptionsToMap = (exemptionsList) => {
    let exemptionsMap = {};
    if (exemptionsList) {
        exemptionsMap = exemptionsList.reduce((obj, keyField) => {
            obj[keyField.checkName] = keyField;
            return obj;
        }, {});
    }
    return exemptionsMap;
};
export const getIsExempt = (checkName, linterExemptions) => {
    let exemptionsMap = convertLinterExemptionsToMap(linterExemptions);
    return (exemptionsMap &&
        exemptionsMap[checkName] &&
        exemptionsMap[checkName].isExempt);
};
// https://atlassian.design/guidelines/brand/color-1
const getScoreColorByPercentage = (perc) => {
    if (isNaN(perc)) {
        return "#6C798F"; //  N200 - Bling bling
    }
    if (perc < 50) {
        return "#DE350B"; //  R400 - Red dirt
    }
    if (perc < 80) {
        return "#FF991F"; //  Y400 - Cheezy blasters
    }
    return "#00875A"; //  G400 - Slime
};
const getCheckScore = (value, max_value, outcome) => {
    let checkScore = "-";
    if (outcome === OUTCOME_COMPLETE) {
        checkScore = `${value}/${max_value}`;
    }
    if (outcome === OUTCOME_NOT_APPLICABLE) {
        checkScore = "N/A";
    }
    return (React.createElement(CheckScore, null, checkScore));
};
export class LinterView extends React.Component {
    constructor(props) {
        super(props);
        this.openRerunModal = () => this.setState({ isRerunChecksModalOpen: true });
        this.closeRerunModal = async () => this.setState({ isRerunChecksModalOpen: false });
        this.openSettingsModal = () => this.setState({ isSettingsModalOpen: true });
        this.rerunChecks = async () => {
            const service = this.props.data.service;
            try {
                let resp = await this.props.sendLinterJobExecReq({
                    variables: {
                        service: service.id,
                        task: "all",
                    },
                });
                if (resp && resp.data) {
                    if (resp.data.sendLinterJobExecReq.statusCode === 200 && !resp.errors) {
                        this.props.showFlag({
                            type: "success",
                            title: "Re-run of Checks Submitted",
                            message: "The re-run of checks for " +
                                service.id +
                                " should start shortly, please wait a few minutes for results",
                        });
                    }
                    else {
                        this.props.showFlag({
                            type: "error",
                            title: "Error scheduling re-run",
                            message: "Could not schedule the re-run, error: " +
                                resp.data.sendLinterJobExecReq.message,
                        });
                    }
                    this.setState({ isRerunChecksModalOpen: false });
                    this.props.track("linter job successfully finished", {
                        category: "info",
                    });
                }
            }
            catch (e) {
                console.error(e);
                this.props.track("linter job failed", { category: "error" });
                if (e.graphQLErrors &&
                    e.graphQLErrors[0].message.includes("AlreadyExists")) {
                    this.props.showFlag({
                        type: "error",
                        title: "Linter Error",
                        message: "A re-check is already scheduled for " +
                            service.id +
                            ". Please try again later.",
                    });
                }
                else {
                    this.props.showFlag({
                        type: "error",
                        title: "Error",
                        message: e.toString(),
                    });
                }
            }
        };
        this.getFullDistinguishCheckName = (checkName, scores) => {
            let fullDistinguishCheckName = "";
            scores.forEach((score) => {
                const extractedCheckName = score.checkName.substring(score.checkName.indexOf(".") + 1, score.checkName.indexOf(".", score.checkName.indexOf(".") + 1));
                if (extractedCheckName === checkName) {
                    fullDistinguishCheckName = score.checkName;
                }
            });
            if (!fullDistinguishCheckName) {
                console.error("Could not retrieve a score with the same check name from the exemption check name: ", checkName);
            }
            return fullDistinguishCheckName;
        };
        this.getOutcomeIcon = (score, maxValue, outcome, checkName, linterExemptions) => {
            const isExempt = getIsExempt(checkName, linterExemptions);
            if (outcome === OUTCOME_NOT_APPLICABLE) {
                return "";
            }
            else if (isExempt) {
                return (React.createElement(TableIcon, { title: "Check exempted" },
                    React.createElement(CrossCircleIcon, { size: "medium", primaryColor: "#DFE1E6" // N40 - Jolly fun time
                        , label: "Check exempted" })));
            }
            else if (outcome === OUTCOME_ERROR) {
                return (React.createElement(TableIcon, { title: "Check error" },
                    React.createElement(ErrorIcon, { size: "medium", primaryColor: "#FF991F" // Y400 - Cheezy blasters
                        , label: "Check error" })));
            }
            else if (getScorePercent(score, maxValue) === 100 &&
                outcome === OUTCOME_COMPLETE) {
                return (React.createElement(TableIcon, null,
                    React.createElement(CheckCircleIcon, { size: "medium", primaryColor: "#00875A" // G400 - Slime
                        , label: "Perfect score! Well done!" })));
            }
            else if (getScorePercent(score, maxValue) < 100 &&
                outcome === OUTCOME_COMPLETE) {
                return (React.createElement(TableIcon, null,
                    React.createElement(JiraFailedBuildStatusIcon, { size: "medium", primaryColor: "#DE350B" // R400 - Red dirt
                        , label: "Score needs work" })));
            }
            return "";
        };
        this.getOrderKey = (scoreValue, scoreMaxValue, scoreOutcome, scoreCheckName, linterExemptions) => {
            let orderKey = 0;
            // order: OUTCOME_COMPLETE, OUTCOME_ERROR, OUTCOME_NOT_APPLICABLE, EXEMPT
            if (getIsExempt(scoreCheckName, linterExemptions)) {
                orderKey = Number.MIN_SAFE_INTEGER;
            }
            else if (scoreOutcome === OUTCOME_NOT_APPLICABLE) {
                orderKey = Number.MIN_SAFE_INTEGER / 2;
            }
            else if (scoreOutcome === OUTCOME_ERROR) {
                orderKey = Number.MIN_SAFE_INTEGER / 4;
            }
            else {
                orderKey = scoreMaxValue - scoreValue;
            }
            return orderKey + 10000; // KNOWN BUG AK-5808: Atlaskit's DynamicTable doesn't sort falsy values like 0 properly
        };
        this.state = {
            isRerunChecksModalOpen: false,
            isSettingsModalOpen: false,
        };
    }
    shouldComponentUpdate() {
        return true;
    }
    componentDidMount() {
        document.title = this.props.data.service.id + " | Linter";
    }
    componentWillUnmount() {
        document.title = "Microscope";
    }
    render() {
        const { getLinterScoresByServiceName: scoresAsString, getLinterOverallScoreByServiceName: overallScoreAsString, getLinterExemptionsByServiceName: linterExemptionsAsString, } = this.props.data;
        const service = this.props.data.service;
        const scores = scoresAsString.map((score) => ({
            checkName: score.checkName,
            value: Number(score.value),
            maxValue: Number(score.maxValue),
            outcome: Number(score.outcome),
            dateRan: score.dateRan,
            details: score.details,
        }));
        const overallScore = {
            value: Number(overallScoreAsString.value),
            minValue: Number(overallScoreAsString.minValue),
            maxValue: Number(overallScoreAsString.maxValue),
        };
        const linterExemptions = linterExemptionsAsString.linterExemption.map((exemption) => ({
            id: this.getFullDistinguishCheckName(exemption.id, scores),
            checkName: this.getFullDistinguishCheckName(exemption.checkName, scores),
            isExempt: exemption.isExempt,
            reason: exemption.reason,
            submittedBy: exemption.submittedBy,
        }));
        const score = getScorePercent(overallScore.value, overallScore.maxValue);
        const greenScores = scores.filter((score) => getScorePercent(score.value, score.maxValue) === 100 &&
            !getIsExempt(score.checkName, linterExemptions) &&
            score.outcome === OUTCOME_COMPLETE);
        const redScores = scores.filter((score) => getScorePercent(score.value, score.maxValue) < 100 &&
            !getIsExempt(score.checkName, linterExemptions) &&
            score.outcome === OUTCOME_COMPLETE);
        const notApplicableScores = scores.filter((score) => score.outcome === OUTCOME_NOT_APPLICABLE);
        const errorScores = scores.filter((score) => score.outcome === OUTCOME_ERROR);
        const { isRerunChecksModalOpen, isSettingsModalOpen } = this.state;
        const exemptionsDisabled = !!this.props.flags.linterPageExemptionsDisabled;
        const actions = (React.createElement(ButtonGroup, null,
            React.createElement(ControlButtonDiv, null,
                React.createElement(Button, { onClick: this.openRerunModal, appearance: "primary", iconBefore: React.createElement(RefreshIcon, { size: "medium", label: "Re-run Checks for this service" }) }, "Re-run Checks for this service")),
            !exemptionsDisabled && (React.createElement(Button, { onClick: this.openSettingsModal, appearance: "primary", iconBefore: React.createElement(SettingsIcon, { size: "medium", label: "Settings" }) }, "Settings"))));
        const noScoresReturned = scores.length === 0;
        let scoresTable = null;
        if (!noScoresReturned) {
            let caption = (React.createElement("span", null,
                React.createElement(CheckCounter, null,
                    React.createElement("br", null),
                    React.createElement("span", { className: "badge-red" },
                        React.createElement(Badge, { max: 100, appearance: "important" }, redScores.length)),
                    "\u00A0 action items \u00A0\u00A0",
                    React.createElement("span", { className: "badge-green" },
                        React.createElement(Badge, { max: 100, appearance: "added" }, greenScores.length)),
                    "\u00A0 passed \u00A0\u00A0",
                    React.createElement("span", { className: "badge-grey" },
                        React.createElement(Badge, { max: 100, appearance: "default" }, errorScores.length)),
                    "\u00A0 errors \u00A0\u00A0",
                    React.createElement("span", { className: "badge-grey" },
                        React.createElement(Badge, { max: 100, appearance: "default" }, notApplicableScores.length)),
                    "\u00A0 not applicable")));
            let scoresHead = {
                cells: [
                    {
                        key: "checkname",
                        content: "Check",
                        isSortable: true,
                        shouldTruncate: false,
                    },
                    {
                        key: "componentname",
                        content: "Component",
                        isSortable: true,
                        shouldTruncate: false,
                    },
                    {
                        key: "date",
                        content: "Executed",
                        isSortable: true,
                        shouldTruncate: false,
                        width: 15,
                    },
                    { key: "score", content: "Score", isSortable: true, width: 5 },
                    { key: "colour", content: "", width: 1 },
                ],
            };
            let scoresCells = scores.map((score) => ({
                cells: [
                    {
                        key: score.checkName.split(".")[1],
                        content: (React.createElement("span", null,
                            React.createElement(CheckName, null,
                                React.createElement(Link, { to: routes.checkDocsUrl(score.checkName) }, prettifyCheckName(score.checkName.split(".")[1]))),
                            React.createElement("br", null),
                            React.createElement(CheckDetails, null, score.details))),
                    },
                    {
                        key: score.checkName.split(".")[0],
                        content: React.createElement(CheckName, null, score.checkName.split(".")[0]),
                    },
                    {
                        key: score.dateRan,
                        content: formatDate(score.dateRan),
                    },
                    {
                        key: this.getOrderKey(score.value, score.maxValue, score.outcome, score.checkName, linterExemptions),
                        // show a score of N/A if we got OUTCOME_NOT_APPLICABLE or OUTCOME_ERROR
                        content: getCheckScore(score.value, score.maxValue, score.outcome),
                    },
                    {
                        key: score.outcome,
                        content: this.getOutcomeIcon(score.value, score.maxValue, score.outcome, score.checkName, linterExemptions),
                    },
                ],
            }));
            scoresTable = (React.createElement(DynamicTable, { caption: caption, head: scoresHead, rows: scoresCells, rowsPerPage: 10, defaultPage: 1, loadingSpinnerSize: "large", isLoading: false, isFixedSize: false, defaultSortKey: "score", defaultSortOrder: "DESC" }));
        }
        else {
            scoresTable = (React.createElement(BoldSpan, null,
                "No checks have been run for this service yet. Linter checks are run once every 24 hours.",
                React.createElement("br", null),
                "Please ping the Service Linter Stride room if your results do not appear within 24 hours."));
        }
        return (React.createElement(PageLayout, { title: "Linter Score", actions: actions },
            React.createElement(Banner, { isOpen: true, appearance: "announcement", icon: React.createElement(SendIcon, { label: "", secondaryColor: "inherit" }) },
                "For any help/queries about your linter score contact us at ",
                React.createElement("a", { href: `https://atlassian.slack.com/archives/CFHSVTG85` }, "#help-service-linter")),
            React.createElement(OverallScore, null,
                React.createElement(ScoreSpan, { color: getScoreColorByPercentage(score) }, !isNaN(score) ? score + "%" : "No score")),
            React.createElement("div", null, scoresTable),
            isRerunChecksModalOpen && (React.createElement(Modal, { isOpen: isRerunChecksModalOpen, heading: `Are you sure you want to re-run Checks for ${service.id}`, actions: [
                    { text: "Re-run Checks", onClick: this.rerunChecks },
                    { text: "Cancel", onClick: this.closeRerunModal },
                ], onClose: this.closeRerunModal }, "Linter checks are executed every 24 hours without user intervention.")),
            isSettingsModalOpen && (React.createElement(Exemptions, { linterExemptions: linterExemptions, serviceName: service.id, scores: scores, onClose: () => this.setState({ isSettingsModalOpen: false }) }))));
    }
}
export const LinterPage = compose(graphql(serviceLinterScoresQuery, {
    options: () => ({
        errorPolicy: "all",
    }),
}), graphql(sendLinterJobExecReqMutation, {
    name: "sendLinterJobExecReq",
}), withAnalytics, withLoadingSpinner, withShowFlag, withFeatureFlags, withGraphqlErrorFlags, withServiceNotFoundHandler)(LinterView);
